<script setup lang="ts">
import { APP_VERSION } from '@bf/domain'
import { computed } from 'vue'
import type { CSSProperties } from 'vue'
import type { MediaAsset } from '@bf/domain'
import { RouterLink } from 'vue-router'
import { useShowroomStore } from '../store/showroom'

const store = useShowroomStore()
const draft = computed(() => store.state.draft)
const generatedAt = computed(() => {
  if (!draft.value?.createdAt) return '待生成'

  const createdAt = new Date(draft.value.createdAt)
  if (Number.isNaN(createdAt.getTime())) return '时间格式无效'

  return new Intl.DateTimeFormat('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(createdAt)
})
const selectedItems = computed(() =>
  (draft.value?.items ?? [])
    .map((item) => ({
      item,
      entity: store.getEntity(item.entityId)
    }))
    .filter(
      (
        entry
      ): entry is {
        item: NonNullable<typeof draft.value>['items'][number]
        entity: NonNullable<ReturnType<typeof store.getEntity>>
      } => entry.entity !== undefined
    )
)
const selectedStyle = computed(() => {
  const entry = selectedItems.value.find(({ entity }) => entity.contentType === 'software-style')
  return entry?.entity.contentType === 'software-style' ? { item: entry.item, entity: entry.entity } : null
})
const supportingItems = computed(() =>
  selectedItems.value.filter(({ entity }) => entity.contentType !== 'software-style')
)
const themeVars = computed<CSSProperties>(() => {
  const palette = selectedStyle.value?.entity.palette
  if (palette === undefined) return {}
  return {
    '--solution-theme-bg': palette.background,
    '--solution-theme-surface': palette.surface,
    '--solution-theme-primary': palette.primary,
    '--solution-theme-accent': palette.accent,
    '--solution-theme-text': palette.text
  } as CSSProperties
})

function imageAsset(mediaId: string): MediaAsset | undefined {
  const asset = store.getAsset(mediaId)
  return asset?.mediaType === 'image' ? asset : undefined
}

function customerPageLabel(label: string) {
  return label.replace(/（编号映射）/g, '')
}
</script>

<template>
  <section class="solution-preview" :class="{ 'solution-themed': selectedStyle }" :style="themeVars">
    <header class="solution-cover">
      <div class="cover-mark">BF</div>
      <div>
        <p class="eyebrow">SOLUTION PRESENTATION</p>
        <h2>{{ draft?.projectLabel || '楼宇自控方案交流' }}</h2>
        <p>{{ draft?.customerLabel || '客户现场演示' }}</p>
        <span v-if="selectedStyle" class="solution-cover-style">
          界面风格 · {{ selectedStyle.entity.title }}
        </span>
      </div>
      <div class="cover-meta">
        <span>v{{ APP_VERSION }} 方案预览 / 非报价</span>
        <small>内容版本 {{ draft?.contentVersion || '待加载' }}</small>
        <small>
          <time :datetime="draft?.createdAt">生成时间 {{ generatedAt }}</time>
        </small>
        <small>有效期 待商务确认</small>
      </div>
    </header>

    <div v-if="selectedStyle" class="software-solution-package">
      <header>
        <div>
          <p>01 / SOFTWARE VISUAL SYSTEM</p>
          <h3>{{ selectedStyle.entity.title }}</h3>
          <span>{{ selectedStyle.entity.summary }}</span>
        </div>
        <div class="software-solution-completeness">
          <strong>{{ selectedStyle.entity.pagePreviews.length }} / 10</strong>
          <span>业务画面完整覆盖</span>
        </div>
      </header>

      <div class="software-solution-details">
        <div>
          <span>统一色板</span>
          <div class="solution-palette">
            <i
              v-for="(color, name) in selectedStyle.entity.palette"
              :key="name"
              :style="{ background: color }"
              :title="`${name}: ${color}`"
            />
          </div>
        </div>
        <div>
          <span>推荐场景</span>
          <p>{{ selectedStyle.entity.recommendedFor.join(' · ') }}</p>
        </div>
      </div>

      <div class="software-solution-pages">
        <article v-for="(page, index) in selectedStyle.entity.pagePreviews" :key="page.pageKey">
          <figure>
            <img
              v-if="imageAsset(page.mediaId)"
              :src="store.mediaUrl(imageAsset(page.mediaId)!)"
              :alt="`${selectedStyle.entity.title} · ${customerPageLabel(page.pageLabel)}业务画面预览`"
            />
            <div v-else class="media-fallback"><strong>UI</strong><span>预览待接入</span></div>
          </figure>
          <div>
            <span>{{ String(index + 1).padStart(2, '0') }}</span>
            <div>
              <strong>{{ customerPageLabel(page.pageLabel) }}</strong>
              <p>{{ page.customerIntro }}</p>
            </div>
          </div>
        </article>
      </div>

      <div class="software-solution-scope" role="note">
        <strong>演示范围说明</strong>
        <p>当前为软件功能与视觉方案演示，正式范围以设备清单、点表、通讯协议、控制策略、权限矩阵及实施文件为准。</p>
      </div>
    </div>

    <div v-else class="solution-style-missing" role="alert">
      <span>!</span>
      <div>
        <strong>方案不完整：尚未选择软件风格</strong>
        <p>请返回方案篮，从 8 套完整风格中选择一套后再生成客户预览。</p>
      </div>
      <RouterLink class="button button-primary" :to="{ name: 'catalog', params: { section: 'software' } }">
        选择软件风格
      </RouterLink>
    </div>

    <div v-if="supportingItems.length" class="solution-body">
      <div class="solution-supporting-heading">
        <span>02 / OPTIONAL CONTENT</span>
        <h3>补充展示内容</h3>
      </div>
      <article v-for="({ item, entity }, index) in supportingItems" :key="item.itemId">
        <span class="solution-index">{{ String(index + 1).padStart(2, '0') }}</span>
        <div>
          <small>
            {{ item.configuration?.kind === 'cabinet-layout' ? 'cabinet configuration' : entity.contentType }}
          </small>
          <h3>{{ item.title }}</h3>
          <p>{{ entity.summary }}</p>
          <dl v-if="item.configuration?.kind === 'cabinet-layout'" class="solution-configuration">
            <div>
              <dt>点位口径</dt>
              <dd>{{ item.configuration.pointCount }} 点 · {{ item.configuration.pointBasis }}</dd>
            </div>
            <div>
              <dt>模拟布局</dt>
              <dd>{{ item.configuration.layoutTemplateId }}</dd>
            </div>
            <div>
              <dt>估算模块</dt>
              <dd>
                IO {{ item.configuration.estimatedIoModules }} 组 ·
                端子 {{ item.configuration.estimatedTerminalGroups }} 组 ·
                预留 {{ item.configuration.reservePercent }}%
              </dd>
            </div>
          </dl>
          <ul v-if="item.configuration?.kind === 'cabinet-layout'" class="configuration-assumptions">
            <li v-for="assumption in item.configuration.assumptions" :key="assumption">
              {{ assumption }}
            </li>
          </ul>
          <span v-if="'demoData' in entity && entity.demoData" class="badge badge-warning">
            虚构演示数据
          </span>
        </div>
      </article>
    </div>

    <footer class="solution-footer">
      <div>
        <strong>v{{ APP_VERSION }} 方案预览说明</strong>
        <p>本页仅用于方案交流预览，不构成报价、合同、设计或技术承诺；当前版本不提供 PDF 输出。</p>
        <p>方案假设、适用边界与排除项均待进一步确认，占位内容不得作为交付依据。</p>
      </div>
      <RouterLink class="button button-secondary" :to="{ name: 'basket' }">
        返回调整方案
      </RouterLink>
    </footer>
  </section>
</template>
